import { useMemo } from 'react';

import {
  getAvailableUnaddressedMailingProducts,
  type UnaddressedMailingProduct,
  type UnaddressedMailingType,
} from '@/services/ws-post/src/main/resources/rest-api';
import type { DateTimeUnixType } from '@/js/libs/api-helpers';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { useFeatureToggleContext } from 'src/modules/FeatureToggle/index.ts';
import { getPostDtoDate } from '../helpers.ts';
import { getProductsForUnaddressedMailingType } from '../unaddressedMailingProduct.ts';

export const useAvailableUnaddressedMailingProducts = (
  mailingType: UnaddressedMailingType | undefined,
  distributionDate?: DateTimeUnixType,
): { products: UnaddressedMailingProduct[]; pending: boolean; isCatalogLoaded: boolean } => {
  const { isAdditionalPromopostProductsEnabled } = useFeatureToggleContext();

  const requestParams = useMemo(() => {
    if (!isAdditionalPromopostProductsEnabled) {
      return {};
    }

    const distributionDateParam = getPostDtoDate(distributionDate);

    if (!distributionDateParam) {
      return {};
    }

    return { distributionDate: distributionDateParam };
  }, [distributionDate, isAdditionalPromopostProductsEnabled]);

  const availableUnaddressedMailingProductsRequest = useRequest({
    apiMethod: getAvailableUnaddressedMailingProducts,
    params: requestParams,
  });

  const availableProducts = useSubscribe(availableUnaddressedMailingProductsRequest.data$, (response) => response?.data);
  const pending = useSubscribe(availableUnaddressedMailingProductsRequest.pending$);

  const products = useMemo(() => getProductsForUnaddressedMailingType(availableProducts, mailingType), [availableProducts, mailingType]);

  return { products, pending, isCatalogLoaded: availableProducts !== undefined };
};
